import { useMemo } from 'react'
import { COMMODITY_CLASS } from 'utils/constants'
import { CommodityError } from 'components/containers/bills/components/CommodityTypeCostThresholdAlert'

/**
 * Props for the hook
 */
interface UseCostThresholdAlertProps {
  inboundTicketData: {
    materials: any[]
  } | null
  hasFerrousCostThreshold: boolean
  hasNonFerrousCostThreshold: boolean
  ferrousTicketValueThreshold: string | number
  nonFerrousTicketValueThreshold: string | number
  paymentMethods: any[]
  ferrousAllowedPaymentTypes: string[]
  nonFerrousAllowedPaymentTypes: string[]
  commoditiesData: Record<string, any> | null
}

interface UseCostThresholdAlertResult {
  costThresholdErrors: CommodityError[]
  showCostThresholdAlert: boolean
}

const useCostThresholdAlert = ({
  inboundTicketData,
  hasFerrousCostThreshold,
  hasNonFerrousCostThreshold,
  ferrousTicketValueThreshold,
  nonFerrousTicketValueThreshold,
  paymentMethods,
  ferrousAllowedPaymentTypes,
  nonFerrousAllowedPaymentTypes,
  commoditiesData,
}: UseCostThresholdAlertProps): UseCostThresholdAlertResult => {
  const { costThresholdErrors, showCostThresholdAlert } = useMemo(() => {
    if (!inboundTicketData?.materials || !commoditiesData) {
      return { costThresholdErrors: [], showCostThresholdAlert: false }
    }

    const errors: CommodityError[] = []

    // Calculate ferrous value
    const { ferrousValue, ferrousCommodityNames } = inboundTicketData.materials.reduce(
      (acc, material) => {
        const commodityClass = material?.materialType?.commodity?.class
        // If the material is not ferrous or has opted out of compliance, skip it
        if (commodityClass !== COMMODITY_CLASS.FERROUS || material?.materialType?.complianceOptOut) return acc
        const netValue =
          material.weightAndPrice.netValue.amount / Math.pow(10, material.weightAndPrice.netValue.precision)
        return {
          ferrousValue: acc.ferrousValue + netValue,
          ferrousCommodityNames: [...acc.ferrousCommodityNames, material?.materialType?.commodity?.name],
        }
      },
      { ferrousValue: 0, ferrousCommodityNames: [] },
    )

    // Calculate non-ferrous value
    const { nonFerrousValue, nonFerrousCommodityNames } = inboundTicketData.materials.reduce(
      (acc, material) => {
        const commodityClass = material?.materialType?.commodity?.class
        // If the material is not non-ferrous (is ferrous) or has opted out of compliance, skip it
        if (commodityClass !== COMMODITY_CLASS.NON_FERROUS || material?.materialType?.complianceOptOut) return acc
        const netValue =
          material.weightAndPrice.netValue.amount / Math.pow(10, material.weightAndPrice.netValue.precision)
        return {
          nonFerrousValue: acc.nonFerrousValue + netValue,
          nonFerrousCommodityNames: [...acc.nonFerrousCommodityNames, material?.materialType?.commodity?.name],
        }
      },
      { nonFerrousValue: 0, nonFerrousCommodityNames: [] },
    )

    // Check ferrous threshold
    if (
      hasFerrousCostThreshold &&
      ferrousValue >= Number(ferrousTicketValueThreshold) &&
      paymentMethods.some(payment => !ferrousAllowedPaymentTypes.includes(payment.method))
    ) {
      errors.push({
        commodityId: 'ferrous',
        commodityName:
          ferrousCommodityNames.slice(0, 3).join(', ') +
          (ferrousCommodityNames.length > 3 ? ' and ' + (ferrousCommodityNames.length - 3) + ' others' : ''),
        threshold: Number(ferrousTicketValueThreshold),
        value: ferrousValue,
        type: 'FERROUS',
      })
    }

    // Check non-ferrous threshold
    if (
      hasNonFerrousCostThreshold &&
      nonFerrousValue >= Number(nonFerrousTicketValueThreshold) &&
      paymentMethods.some(payment => !nonFerrousAllowedPaymentTypes.includes(payment.method))
    ) {
      errors.push({
        commodityId: 'non-ferrous',
        commodityName:
          nonFerrousCommodityNames.slice(0, 3).join(', ') +
          (nonFerrousCommodityNames.length > 3 ? ' and ' + (nonFerrousCommodityNames.length - 3) + ' others' : ''),
        threshold: Number(nonFerrousTicketValueThreshold),
        value: nonFerrousValue,
        type: 'NON_FERROUS',
      })
    }

    return {
      costThresholdErrors: errors,
      showCostThresholdAlert: errors.length > 0,
    }
  }, [
    inboundTicketData,
    hasFerrousCostThreshold,
    hasNonFerrousCostThreshold,
    ferrousTicketValueThreshold,
    nonFerrousTicketValueThreshold,
    paymentMethods,
    ferrousAllowedPaymentTypes,
    nonFerrousAllowedPaymentTypes,
    commoditiesData,
  ])

  return { costThresholdErrors, showCostThresholdAlert }
}

export default useCostThresholdAlert
